import { useState, useEffect, useRef, useCallback } from 'react'

interface HealthState {
  status: 'online' | 'offline' | 'reconnecting'
  lastOnline: number
  downSince: number | null
  reconnectAttempt: number
}

export function useBackendHealth(checkIntervalMs = 15000) {
  const [health, setHealth] = useState<HealthState>({
    status: 'online',
    lastOnline: Date.now(),
    downSince: null,
    reconnectAttempt: 0
  })
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const fastPollRef = useRef<NodeJS.Timeout | null>(null)
  const wasOfflineRef = useRef(false)

  const checkHealth = useCallback(async () => {
    try {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 5000)
      const res = await fetch('/api/health', { signal: controller.signal })
      clearTimeout(timeout)

      if (res.ok) {
        const wasOffline = wasOfflineRef.current
        wasOfflineRef.current = false

        setHealth({
          status: 'online',
          lastOnline: Date.now(),
          downSince: null,
          reconnectAttempt: 0
        })

        // Se estava offline e voltou, parar fast poll
        if (wasOffline && fastPollRef.current) {
          clearInterval(fastPollRef.current)
          fastPollRef.current = null
        }

        return true
      }
      throw new Error(`HTTP ${res.status}`)
    } catch {
      wasOfflineRef.current = true

      setHealth(prev => ({
        status: prev.downSince ? 'reconnecting' : 'offline',
        lastOnline: prev.lastOnline,
        downSince: prev.downSince || Date.now(),
        reconnectAttempt: prev.reconnectAttempt + 1
      }))

      // Iniciar fast polling (cada 3s) para detectar quando volta
      if (!fastPollRef.current) {
        fastPollRef.current = setInterval(async () => {
          try {
            const controller = new AbortController()
            const timeout = setTimeout(() => controller.abort(), 3000)
            const res = await fetch('/api/health', { signal: controller.signal })
            clearTimeout(timeout)
            if (res.ok) {
              // Voltou! Limpar fast poll e atualizar estado
              if (fastPollRef.current) {
                clearInterval(fastPollRef.current)
                fastPollRef.current = null
              }
              wasOfflineRef.current = false
              setHealth({
                status: 'online',
                lastOnline: Date.now(),
                downSince: null,
                reconnectAttempt: 0
              })
            }
          } catch { /* still offline */ }
        }, 3000)
      }

      return false
    }
  }, [])

  useEffect(() => {
    // Check immediately
    checkHealth()

    // Regular polling
    intervalRef.current = setInterval(checkHealth, checkIntervalMs)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (fastPollRef.current) clearInterval(fastPollRef.current)
    }
  }, [checkHealth, checkIntervalMs])

  return health
}
